import { FC, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  CircularProgress,
  FormControl,
  InputAdornment,
  MenuItem,
  Stack,
  Typography
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import SearchIcon from '@mui/icons-material/Search'
import {
  StyledInputLabel,
  StyledListSubheader,
  StyledMenuItem,
  StyledSelect,
  StyledTextField
} from 'ui/components/form-components-v2/select-with-popover-menu-v2/selectWithPopoverMenuV2.styles'
import {
  StyledChip,
  StyledSelect as OverflowSelect
} from '../selected-items-display/selectedItemsDisplay.styles'
import { DEFAULT_MENU_PROPS } from '../selected-items-display/selectedItemDisplay.configs'
import { SelectChangeEvent } from '@mui/material/Select'
import { DEFAULT_SELECT_MENU_PROPS } from 'ui/components/form-components-v2/select-with-popover-menu-v2/SelectWithPopoverMenuV2.configs'
import CheckboxV2 from 'ui/components/form-components-v2/checkbox-v2/CheckboxV2'
import { User } from 'models'
import { UserServiceV4 } from 'services'
import useDebounce from 'hooks/useDebounce'
import { seedUsers, useUsersLookup } from 'hooks/useUsersLookup'
import theme from 'theme'

interface RecipientsPickerProps {
  selectedIds: string[]
  onChange: (ids: string[]) => void
  label?: string
  placeholder?: string
}

const RECIPIENT_SEARCH_LIMIT = 100

const RecipientsPicker: FC<RecipientsPickerProps> = ({
  selectedIds,
  onChange,
  label = 'Add recipients',
  placeholder = 'Choose users from the list'
}) => {
  const [searchText, setSearchText] = useState('')
  const [options, setOptions] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const selectRef = useRef<any>(null)
  const { getUser, getUserLabel } = useUsersLookup(selectedIds)

  const debouncedSearch = useDebounce(searchText, 300)

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    const fetchUsers = async () => {
      setIsLoading(true)
      try {
        const { users } = await UserServiceV4.listUsers({
          limit: RECIPIENT_SEARCH_LIMIT,
          search: debouncedSearch || undefined
        })
        // Remembered before anyone can pick one, so ticking a search result never asks the server again.
        seedUsers(users)
        if (cancelled) return
        setOptions(users)
      } catch {
        if (!cancelled) setOptions([])
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    fetchUsers()
    return () => {
      cancelled = true
    }
  }, [debouncedSearch, isOpen])

  const handleOpen = () => {
    setIsOpen(true)
    if (selectRef.current) {
      selectRef.current.classList.add('Mui-focused')
      selectRef.current.previousSibling?.classList.add('Mui-focused')
    }
  }

  const handleClose = () => {
    setIsOpen(false)
    if (selectRef.current) {
      selectRef.current.classList.remove('Mui-focused')
      selectRef.current.previousSibling?.classList.remove('Mui-focused')
    }
    setTimeout(() => setSearchText(''), 0)
  }

  const handleToggle = (userId: string) => {
    if (selectedIds.includes(userId)) {
      onChange(selectedIds.filter((id) => id !== userId))
    } else {
      onChange([...selectedIds, userId])
    }
  }

  const handleRemoveChip = (userId: string) => {
    onChange(selectedIds.filter((id) => id !== userId))
  }

  const loadedIds = useMemo(() => options.map((u) => u.id), [options])
  const allLoadedSelected = loadedIds.length > 0 && loadedIds.every((id) => selectedIds.includes(id))
  const someLoadedSelected = !allLoadedSelected && loadedIds.some((id) => selectedIds.includes(id))

  const handleToggleAllLoaded = () => {
    if (allLoadedSelected) {
      onChange(selectedIds.filter((id) => !loadedIds.includes(id)))
    } else {
      onChange(Array.from(new Set([...selectedIds, ...loadedIds])))
    }
  }

  const renderValue = () => {
    if (selectedIds.length === 0) return placeholder
    return `${selectedIds.length} selected`
  }

  // A recipient we cannot name yet gets no chip, even though the "N selected" count includes them.
  const selectedUsers = useMemo(
    () => selectedIds.map((id) => getUser(id)).filter((u): u is User => Boolean(u)),
    [selectedIds, getUser]
  )

  return (
    <Box
      sx={{
        '& .MuiInputLabel-root': {
          transform: 'translate(14px, -6px) scale(1) !important',
          fontFamily: `${theme.typography.fontFamily.primary} !important`,
          fontSize: `${theme.typography.fontSize.medium} !important`,
          fontStyle: 'normal',
          fontWeight: `${theme.typography.fontWeight.bold} !important`,
          lineHeight: '12px !important',
          color: `${theme.colors.dropdownLabel} !important`,
          backgroundColor: `${theme.colors.labelCardBg} !important`,
          padding: '0 4px !important',
          maxWidth: 'calc(100% - 32px) !important'
        }
      }}
    >
      <FormControl fullWidth>
        <StyledInputLabel id="recipients-label" shrink>{label}</StyledInputLabel>
        <StyledSelect
          ref={selectRef}
          id="recipients-select"
          labelId="recipients-label"
          label={label}
          multiple
          value={selectedIds}
          open={isOpen}
          onOpen={handleOpen}
          onClose={handleClose}
          renderValue={renderValue}
          MenuProps={DEFAULT_SELECT_MENU_PROPS}
          IconComponent={KeyboardArrowDownIcon}
          displayEmpty
          notched
          sx={{
            '& .MuiSelect-select': {
              padding: '14px 40px 14px 16px !important'
            }
          }}
        >
          <StyledListSubheader>
            <StyledTextField
              autoFocus
              fullWidth
              size="small"
              placeholder="Search"
              value={searchText}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.stopPropagation()
                }
              }}
            />
          </StyledListSubheader>
          {isLoading ? (
            <StyledMenuItem className="not-found" sx={{ justifyContent: 'center' }}>
              <CircularProgress size={20} />
            </StyledMenuItem>
          ) : options.length === 0 ? (
            <StyledMenuItem className="not-found">No users found</StyledMenuItem>
          ) : (
            <Box>
              <StyledMenuItem
                onClick={(e) => {
                  e.preventDefault()
                  handleToggleAllLoaded()
                }}
                sx={{ justifyContent: 'flex-start !important' }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CheckboxV2
                    checked={allLoadedSelected}
                    indeterminate={someLoadedSelected}
                    tabIndex={-1}
                    disableFocusRipple
                  />
                  <Typography
                    sx={{ fontSize: theme.typography.fontSize.large, color: theme.colors.menuItemText }}
                  >
                    All users ({options.length})
                  </Typography>
                </Stack>
              </StyledMenuItem>
              {options.map((user) => (
                <StyledMenuItem
                  key={user.id}
                  value={user.id}
                  onClick={() => handleToggle(user.id)}
                  sx={{ justifyContent: 'flex-start !important' }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CheckboxV2
                      checked={selectedIds.includes(user.id)}
                      tabIndex={-1}
                      disableFocusRipple
                    />
                    <Typography
                      sx={{ fontSize: theme.typography.fontSize.large, color: theme.colors.menuItemText }}
                    >
                      {getUserLabel(user.id)}
                    </Typography>
                  </Stack>
                </StyledMenuItem>
              ))}
            </Box>
          )}
        </StyledSelect>
      </FormControl>

      {selectedUsers.length > 0 && (() => {
        const items = selectedUsers.map((u) => ({ id: u.id, label: getUserLabel(u.id) }))
        const firstRowItems = items.length > 3 ? items.slice(0, 3) : items
        const secondRowChip = items.length > 3 ? items[3] : null
        const overflowItems = items.length > 4 ? items.slice(4) : []

        const handleOverflowChange = (event: SelectChangeEvent<unknown>) => {
          const remainingValues = event.target.value as string[]
          const firstRowIds = firstRowItems.map((i) => i.id)
          const fourthId = secondRowChip ? [secondRowChip.id] : []
          const keptIds = new Set([...firstRowIds, ...fourthId, ...remainingValues])
          onChange(selectedIds.filter((id) => keptIds.has(id)))
        }

        return (
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" sx={{ mt: 1.5 }}>
            <Stack width="100%" direction="row" gap={1} flexWrap="nowrap" alignItems="center">
              {firstRowItems.map((item) => (
                <StyledChip
                  key={item.id}
                  label={
                    <span className="chip-label">
                      <span className="chip-label-content">{item.label}</span>
                    </span>
                  }
                  className={firstRowItems.length > 1 ? 'clipped-chip' : ''}
                  onDelete={() => handleRemoveChip(item.id)}
                  deleteIcon={<CloseIcon />}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    maxWidth: `${100 / firstRowItems.length}% !important`,
                    justifyContent: 'space-between',
                    '& .MuiChip-label': { flex: 1 },
                    '& .chip-label': { width: '100%', justifyContent: 'space-between' },
                    '& .chip-label-content': { marginRight: '16px !important' }
                  }}
                />
              ))}
            </Stack>
            {secondRowChip && (
              <StyledChip
                key={secondRowChip.id}
                label={
                  <span className="chip-label">
                    <span className="chip-label-content">{secondRowChip.label}</span>
                  </span>
                }
                onDelete={() => handleRemoveChip(secondRowChip.id)}
                deleteIcon={<CloseIcon />}
                sx={{
                  justifyContent: 'space-between',
                  '& .MuiChip-label': { flex: 1 },
                  '& .chip-label': { width: '100%', justifyContent: 'space-between' },
                  '& .chip-label-content': { marginRight: '16px !important' }
                }}
              />
            )}
            {overflowItems.length > 0 && (
              <OverflowSelect
                multiple
                value={overflowItems.map((i) => i.id)}
                onChange={handleOverflowChange}
                renderValue={(selected) => `+${(selected as string[]).length}`}
                MenuProps={DEFAULT_MENU_PROPS}
                IconComponent={KeyboardArrowDownIcon}
                displayEmpty
                sx={{
                  '& .MuiSelect-select': {
                    padding: '14px 40px 14px 16px !important'
                  }
                }}
              >
                {overflowItems.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    <StyledChip
                      label={
                        <span className="chip-label">
                          <span className="chip-label-content">{item.label}</span>
                        </span>
                      }
                      onDelete={(e) => {
                        e.stopPropagation()
                        handleRemoveChip(item.id)
                      }}
                      deleteIcon={<CloseIcon />}
                      className="select-item-chip"
                      sx={{ '& .chip-label-content': { marginRight: '16px !important' } }}
                    />
                  </MenuItem>
                ))}
              </OverflowSelect>
            )}
          </Stack>
        )
      })()}
    </Box>
  )
}

export default RecipientsPicker
